import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  clientsApi,
  productsApi,
  profileClientApi,
  brandsApi,
  ordersApi,
  administrativeApi,
  School,
  invoicingApi,
  scheduledOrdersApi,
  simulatedOrdersApi,
  couponApi,
} from "@/lib/api";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { getAccountIdSync, getUserEmailSync, userManager } from "@/lib/oidcAuth";
import { initializeSegmentOrder, getSegmentOrder } from "@/lib/segmentApi";
import { useSmartErrorRecovery } from "@/hooks/useSmartErrorRecovery";
import { useNativePDFGenerator } from "@/hooks/useNativePDFGenerator";
import { useRegeneratePDF } from "@/hooks/useRegeneratePDF";
import { useLocation, Link, useNavigate } from "react-router-dom";
import ClientSelector from "./ClientSelector";
import OrderTimeline from "./OrderTimeline";
import OrderProgress from "./OrderProgress";
import PDFSummary from "./PDFSummary";
import { StatusModal } from "./StatusModal";
import { CsatRatingModal } from "./CsatRatingModal";
import { useStatusModal } from "@/hooks/useStatusModal";
import { useOrderCalculations } from "@/hooks/useOrderCalculations";
import { TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "@/components/ui/sonner";
import { useUser } from "@/contexts/UserContext";
import { useCycleRequiredHandler } from "@/hooks/useCycleRequiredHandler";
import {
  isInvalidCycleCodeFormatError,
  isCycleNotConfiguredError,
  isCycleNotOpenError,
} from "@/lib/cycleError";
import { buildCycleCode, resolveOrderCycleYear, resolveCycleYearFromCode, resolveCycleFlow, getSecondSemesterAdjustmentDeadline } from "@/lib/orderCycle";
import { CyclePickerDialog } from "@/components/shared/CyclePickerDialog";
import { OrderShippingAddressSelect } from "@/components/shared/address/OrderShippingAddressSelect";
import { useOrderShippingAddress } from "@/components/shared/address/useOrderShippingAddress";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";
import { Package, School as SchoolIcon, Eye, Lock, ExternalLink } from "lucide-react";
import { usePDFZipper } from "@/hooks/usePDFZipper";
import { mapEtapaToGradeName, mapEtapaToSegment, applyProductBonuses, isProgrammedOrderItem, isDeferredProgrammedOrderItem, couponBucketForItem } from "@/lib/utils";
import { Label } from "../ui/label";
import { useQuery } from '@tanstack/react-query';
import { formatCurrency } from "@/lib/formatters";
import { isProductBonusEnabledForCycle } from "@/features/productBonusFeature";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";
import type { Client, OrderItem, ProgramadoDto, CompletedOrderData, ProcessingProgress, OrderType } from "./types";
import type { CouponOrderReference, CouponValidation, ScheduledOrderItemTrace } from "@/lib/api";
import { buildCouponBonifiedLines, hasCouponUnits, type PdfCouponItem } from "@/lib/couponItems";

const OrderInterface = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedClient, setSelectedClient, openCycleYears, viewOnlyCycleYears } = useUser();
  // Endereço de entrega do pedido (todos os tipos): vai em shipping_address_id de cada pedido criado abaixo.
  const shippingAddress = useOrderShippingAddress(selectedClient?.codigo_cnx);
  const shippingAddressId = shippingAddress.selectedAddress?.id;
  // PDF do pedido: faturamento = padrão de cobrança da escola; entrega = a escolhida (sem ela, o PDF usa o faturamento).
  const pdfAddresses = {
    billingAddress: shippingAddress.billingAddress,
    shippingAddress: shippingAddress.selectedAddress,
  };
  const {
    selectedCycleYear,
    availableCycles,
    dialogOpen: cycleDialogOpen,
    setDialogOpen: setCycleDialogOpen,
    handleCycleError,
    selectCycle,
  } = useCycleRequiredHandler();
  const cnpj = selectedClient?.cnpj ? String(selectedClient.cnpj) : '';

  // Ciclos comerciais disponíveis para pedido: busca a listagem real (GET /admin/cycles) e
  // mostra apenas os abertos (status "Open") dentre o ano atual e o próximo — se o próximo ano
  // ainda não estiver Open, ele simplesmente não aparece como opção.
  const { data: commercialCycles = [] } = useQuery({
    queryKey: ["commercial-cycles-order"],
    queryFn: async () => {
      const response = await clientsApi.getCycles();
      return response.cycles || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const availableOrderCycleYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return commercialCycles
      .filter((cycle) => cycle.status === "Open" && (cycle.year === currentYear || cycle.year === currentYear + 1))
      .map((cycle) => cycle.year)
      .sort((a, b) => a - b);
  }, [commercialCycles]);

  // Ciclos em fechamento (status "Closing"): continuam selecionáveis para o cliente consultar
  // o pedido do ciclo, mas a tela entra em modo somente leitura — sem alterar quantidades nem
  // finalizar pedido (o backend também recusa pedido fora de "Open").
  const readOnlyCycleYears = useMemo(
    () => commercialCycles
      .filter((cycle) => resolveCycleFlow(cycle.status) === "order-readonly")
      .map((cycle) => cycle.year)
      .sort((a, b) => a - b),
    [commercialCycles],
  );

  const selectableCycleYears = useMemo(
    () => [...availableOrderCycleYears, ...readOnlyCycleYears].sort((a, b) => a - b),
    [availableOrderCycleYears, readOnlyCycleYears],
  );

  const isReadOnlyCycle = selectedCycleYear !== null && readOnlyCycleYears.includes(selectedCycleYear);

  // Seleciona automaticamente o ano atual assim que a lista de ciclos abertos chega; se o
  // ano atual não estiver na lista (ainda não aberto), cai para o primeiro ano disponível e,
  // sem nenhum ciclo aberto, para o primeiro ciclo em fechamento (somente leitura).
  useEffect(() => {
    if (selectedCycleYear !== null) return;
    const currentYear = new Date().getFullYear();
    const defaultYear = availableOrderCycleYears.includes(currentYear)
      ? currentYear
      : availableOrderCycleYears[0] ?? readOnlyCycleYears[0];
    if (defaultYear !== undefined) selectCycle(defaultYear);
  }, [availableOrderCycleYears, readOnlyCycleYears, selectedCycleYear, selectCycle]);

  // Pre-carrega as opções de parcelamento logo ao entrar na página
  useQuery({
    queryKey: ["installmentOptions", cnpj, selectedCycleYear ?? null],
    queryFn: async () => {
      const targetDate = new Date();
      targetDate.setMonth(targetDate.getMonth() + 1);
      const response = await invoicingApi.installmentOptions(targetDate.getMonth(), targetDate.getFullYear(), cnpj, selectedCycleYear);
      return response?.data ?? [];
    },
    enabled: !!cnpj,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [schools, setSchools] = useState<School[]>([]);

  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [typeLoading, setTypeLoading] = useState(!!location.state?.typePedido);
  const [scheduledOrderId, setScheduledOrderId] = useState(location.state?.scheduledOrderId || null);
  const [typePedido, setTypePedido] = useState<OrderType | null>(() => {
    if (location.state?.typePedido) {
      return location.state.typePedido;
    }
    return 'NORMAL';
  });
  const [lastScheduled, setLastScheduled] = useState<ProgramadoDto | null>(null);
  // Edição de pedido de Avaliações e Simulados (ciclo 2027+, tipo 00): usa esta mesma tela e o
  // mesmo motor do 2º semestre, mas preserva número e ciclo do próprio pedido.
  const [editedSimulado, setEditedSimulado] = useState<{ orderNumber: string; cycleCode: string } | null>(null);
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [activeSimulatedCampaigns, setActiveSimulatedCampaigns] = useState<any[]>([]);
  const [isCsatModalOpen, setIsCsatModalOpen] = useState(false);
  // Ação executada após o modal de satisfação fechar, quando a edição de um pedido programado é concluída.
  const [postCsatAction, setPostCsatAction] = useState<"return" | "reload" | null>(null);
  const [csatOrderNumber, setCsatOrderNumber] = useState("");
  const [scheduledUserEmail, setScheduledUserEmail] = useState<string | null>(null);

  useEffect(() => {
    setIsScheduled(typePedido === "PROGRAMADO" || typePedido === "SIMULADO" ? true : false);
  }, [typePedido])

  useEffect(() => {
    if (location.state?.typePedido) {
      const timer = setTimeout(() => {
        setTypeLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const smartErrorRecovery = useSmartErrorRecovery();
  const [processingProgress, setProcessingProgress] = useState<ProcessingProgress>({
    isVisible: false,
    currentBatch: 0,
    totalBatches: 0,
    currentItem: 0,
    totalItems: 0,
    message: "",
  });
  const [showPDFSummary, setShowPDFSummary] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");

  const { user } = useUser();

  // Status Modal (only feedback system)
  const [statusModalState, statusModalControls] = useStatusModal();

  const [completedOrderData, setCompletedOrderData] = useState<CompletedOrderData | null>(null);

  // Estado para controle de loading do download
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [selectedAnnualInstallments, setSelectedAnnualInstallments] = useState<number>(0);
  const [selectedSemestralInstallments, setSelectedSemestralInstallments] = useState<number>(0);
  const [selectedAnnualCondition, setSelectedAnnualCondition] = useState<string | null>(null);
  const [selectedSemestralCondition, setSelectedSemestralCondition] = useState<string | null>(null);

  const { generateNativePDF } = useNativePDFGenerator();
  const { generateOrderPdfBlob } = useRegeneratePDF();
  // Mesma geração do PDF da lista/detalhe (2ª via): lê o pedido salvo. Sem o pedido salvo, devolve false e o chamador
  // usa a geração anterior (fallback).
  const savedOrderPdf = async (orderId: string | null | undefined, filename: string): Promise<Blob | false> => {
    if (!orderId) return false;
    try {
      return await generateOrderPdfBlob({ id: orderId } as any, filename);
    } catch (error) {
      console.warn("Não foi possível gerar o PDF a partir do pedido salvo; usando a geração anterior.", error);
      return false;
    }
  };
  const { zipAndSave } = usePDFZipper();



  useEffect(() => {
    const initializeApp = async () => {
      // Initialize segment order from API
      await initializeSegmentOrder();
    };
    initializeApp();
  }, []);

  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      // Fetch clients from backend API using accountId
      const accountId = getAccountIdSync();
      if (!accountId) {
        throw new Error("Account ID not found in session storage");
      }

      await administrativeApi.register();

      const { data } = await clientsApi.getSchoolsByAccount(accountId);

      // Map the clients from schools endpoint to display format
      const filteredClients = (data || [])
        .filter((client: any) => client.clientSchoolIdLex !== null)
        .map((client: any) => ({
          codigo_cnx: client.clientCodeCnx,
          nome_cliente: client.clientFantasyName || client.clientCompanyName,
          shipping_fee: Number(client?.shippingFee) || 0, // Ensure number
          clientSchoolIdLex: client.clientSchoolIdLex,
          cnpj: client.clientCnpj,
          email: client.clientEmail,
          telefone: client.clientPhone,
          endereco: client.clientAddress,
          numero: client.clientNumber,
          bairro: client.clientDistrict,
          cidade: client.clientCity,
          estado: client.clientState,
          cep: client.clientPostCode,
        }));

      // Enhanced deduplication logic
      const map = new Map();
      const uniqueClients = filteredClients.filter((item) => {
        if (!map.has(item.codigo_cnx)) {
          map.set(item.codigo_cnx, true);
          return true;
        }
        return false;
      });

      setClients(uniqueClients);
    } catch (error) {
      console.error("API error fetching clients:", error);
    } finally {
      setClientsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchRequestIdRef = useRef(0);

  const fetchOrderItems = async () => {
    // Guard de sequência: se selectedClient/typePedido/selectedCycleYear mudarem enquanto uma
    // requisição anterior ainda está em voo, a resposta antiga (de outro ciclo comercial) nunca
    // deve sobrescrever o estado — só a requisição mais recente pode gravar em orderItems/etc.
    const requestId = ++fetchRequestIdRef.current;
    const isStale = () => fetchRequestIdRef.current !== requestId;

    if (selectedClient) {
      setProductsLoading(true);
      // Reset states to ensure clean data for the new client
      setOrderItems([]);
      setLastScheduled(null);
      setOrderNumber("");
      setCompletedOrderData(null);

      try {
        // First get client profile data
        const { data } = await profileClientApi.getByClientCode(selectedClient.codigo_cnx, selectedCycleYear ?? undefined);
        if (isStale()) return;
        const clientProfileData = data || [];

        // Only fetch products if we have client profile data
        if (clientProfileData.length === 0) {
          return;
        }

        // Get all products and brands to enrich the client profile data
        const [productResponse, brandResponse] = await Promise.all([
          productsApi.getAll(selectedCycleYear ?? undefined),
          brandsApi.getAll(),
        ]);
        if (isStale()) return;

        const productData = productResponse.data || [];
        const brandData = brandResponse.data || [];

        // Build order items based on client profile data
        const combinedData = clientProfileData
          .map((clientItem: any) => {
            // The API returns productId, not product code
            const productId = clientItem.productId;

            if (!productId) {
              console.warn("No product ID found in client item:", clientItem);
              return null;
            }

            // Find corresponding product data by ID
            const product = productData.find((p) => p.id === productId);

            if (!product) {
              console.warn("Product not found for ID:", productId);
              return null;
            }

            // Use the actual field names from the API response
            const discount = 0;
            const adjustment = clientItem.adjustment || 0;
            const contractedQuantity = clientItem.contractualQuantity || 0;
            const orderedQuantity = clientItem.orderedQuantity || 0;

            // Find brand by name
            const brand = brandData.find((b) => b.name === product.marca);
            const price = clientItem?.productPrice || clientItem.preco || 0;
            // Round to 2 decimal places to ensure consistency with displayed values
            const unitPriceWithDiscount =
              Math.round(price * (1 - discount / 100) * 100) / 100;

            // Inicialmente usar contractedQuantity - será ajustado depois
            const quantidadePedido = contractedQuantity;
            // Round total to 2 decimal places
            const totalValue = Math.round(price * quantidadePedido * 100) / 100;

            return {
              id: product.id,
              product_id: product.id,
              codigo_produto: product.codigoProduto,
              descricao_produto: product.descricaoProduto,
              preco: price,
              ano_letivo: product.anoLetivo || 0,
              marca: product.marca || "",
              sku: product.sku || "",
              status: product.status || "",
              utilizacao: product.utilizacao,
              serie: product.serie,
              segmento: product.segmento,
              quantidade_contratual: contractedQuantity,
              desconto: discount,
              adjustment: adjustment,
              quantidade_pedido: quantidadePedido, // Será ajustado para a maior quantidade da série
              ordered_quantity: orderedQuantity, // Quantidade já pedida anteriormente
              valor_liquido: price,
              valor_total: totalValue,
              delivery_type_id: product.deliveryTypeId || 1,
              delivery_type_number:
                product.deliveryType?.deliveryTypeNumber || 1,
              brand_id: brand?.id || 0,
              product_type: Number(product?.productType) || 0,
              is_printed_product: product.isPrintedProduct,
              previousYearProductPrice: clientItem.previousYearProductPrice ?? null,
              product_migration: clientItem.productMigration ?? null,
              // Additional fields for API
              product_series: product.serie,
              segment_name: product.segmento,
              brand_name: product.marca,
              product_school_year: product.anoLetivo,
              product_sku: product.sku,
              quantity_contracted: contractedQuantity,
              related_product_ids: clientItem.relatedProductIds ?? product.relatedProductIds ?? [],
              quantity_required_for_bonus: clientItem.quantityRequiredForBonus ?? null,
              // Valor cru do backend; é sempre recalculado por applyProductBonuses
              // para itens Professor (ver src/lib/utils.ts).
              bonified_quantity: clientItem.bonifiedQuantity ?? 0,
              used_bonus_quantity: clientItem.usedBonusQuantity ?? 0,
              available_bonus_quantity: clientItem.availableBonusQuantity ?? 0,
              scheduling_category: clientItem.schedulingCategory,
              scheduling_reason: clientItem.schedulingReason,
              scheduled_target_product_id: clientItem.scheduledTargetProductId,
              is_optional: clientItem.isOptional ?? false,
              is_shipment_exempt: clientItem.isShipmentExempt ?? false,
              shipment_exemption_applies: clientItem.shipmentExemptionApplies ?? false,
              scheduled_shipment_freight_base: clientItem.scheduledShipmentFreightBase ?? null,
            };
          })
          .filter((item): item is NonNullable<typeof item> => item !== null); // Remove null entries where products weren't found

        // Sort by etapa-based segment order, then by serie, then by brand (marca)
        const sortedData = combinedData.sort((a, b) => {
          const segmentA = getSegmentOrder(mapEtapaToSegment(a.serie));
          const segmentB = getSegmentOrder(mapEtapaToSegment(b.serie));

          // First, sort by segment
          if (segmentA !== segmentB) return segmentA - segmentB;

          // Second, sort by serie within same segment
          const serieComparison = String(a.serie || "").localeCompare(
            String(b.serie || "")
          );
          if (serieComparison !== 0) return serieComparison;

          // Third, sort by brand (marca) within same serie - ascending order
          const marcaA = String(a.marca || "");
          const marcaB = String(b.marca || "");
          return marcaA.localeCompare(marcaB);
        });

        // Ajustar quantidade_pedido: todos os produtos de uma série devem usar
        // a quantidade do produto com maior quantidade_contratual daquela série
        // INDEPENDENTE do que vier do endpoint
        const seriesMap = new Map<string, number>();

        // Primeiro, encontrar a maior quantidade_contratual por série
        // (produto opcional não define a quantidade da série)
        sortedData.forEach((item) => {
          if (item.is_optional) return;
          const serie = item.serie || "sem_serie";
          const currentMax = seriesMap.get(serie) || 0;

          if (item.quantidade_contratual > currentMax)
            seriesMap.set(serie, item.quantidade_contratual);
        });

        // Verificar se cliente já tem pedidos finalizados para definir comportamento inicial
        let hasExistingOrders = false;
        try {
          const existingOrdersResponse = await ordersApi.getByClient(
            selectedClient.codigo_cnx
          );
          hasExistingOrders =
            existingOrdersResponse.data &&
            existingOrdersResponse.data.length > 0;
          // console.log(
          //   `Cliente ${ selectedClient.codigo_cnx } tem pedidos anteriores:`,
          //   hasExistingOrders
          // );
        } catch (error) {
          console.warn("Erro ao verificar pedidos anteriores:", error);
          // Em caso de erro, assumir que não há pedidos (comportamento seguro)
          hasExistingOrders = false;
        }

        // Depois, aplicar a quantidade máxima da série a todos os produtos dessa série
        // SEMPRE usar a maior quantidade, não importa o que vier do endpoint
        sortedData.forEach((item) => {
          const serie = item.serie || "sem_serie";
          const maxQuantityForSeries = seriesMap.get(serie) || item.quantidade_contratual;
          item.quantidade_pedido = 0;
          // Atualizar quantidade_contratual para refletir a maior quantidade (apenas alunos);
          // o opcional mantém a própria quantidade e é editado isoladamente
          if (!item.is_optional) item.quantidade_contratual = maxQuantityForSeries;
          // Recalcular valores com a nova quantidade
          item.valor_total = Math.round(item.valor_liquido * item.quantidade_pedido * 100) / 100;
        });


        if (typePedido == "PROGRAMADO") {
          /* Pegando pedidos programados - Sempre buscar para possibilitar soma se houver itens de 2º semestre */
          const orderClientResponse = await ordersApi.getByClient(selectedClient.codigo_cnx);
          if (isStale()) return;
          const scheduledAllResponse = await scheduledOrdersApi.getAll();
          if (isStale()) return;
          const scheduledOrderResponse = scheduledOrderId ? await scheduledOrdersApi.getById(scheduledOrderId) : null;
          const authoritativeItemsResponse = scheduledOrderId
            && isNewScheduledOrderCycleEnabledForCycle(selectedCycleYear)
            ? await scheduledOrdersApi.getItems(scheduledOrderId)
            : null;
          if (isStale()) return;
          if (scheduledOrderId) {
            console.log('Pedido programado específico carregado:', scheduledOrderResponse);
          }

          const isSimulatedEditOrder = !!scheduledOrderResponse
            && !!authoritativeItemsResponse?.data
            && String(scheduledOrderResponse.cycleCode || "").endsWith("00");
          setEditedSimulado(isSimulatedEditOrder && scheduledOrderResponse
            ? { orderNumber: scheduledOrderResponse.orderNumber, cycleCode: scheduledOrderResponse.cycleCode }
            : null);

          if (isSimulatedEditOrder && authoritativeItemsResponse?.data) {
            // Itens de simulado são SKUs impressos que não fazem parte do catálogo do cliente:
            // a grade de edição é montada diretamente a partir dos itens do pedido existente.
            const simulatedRows: OrderItem[] = authoritativeItemsResponse.data
              .filter(item => item.quantityOrdered > 0)
              .map((item, index) => ({
                id: -(index + 1),
                product_id: item.productId,
                codigo_produto: item.productCode,
                descricao_produto: item.productDescription,
                preco: item.unitPrice,
                ano_letivo: item.productSchoolYear ?? selectedCycleYear ?? 0,
                marca: item.brandName ?? "",
                sku: item.productSku ?? "",
                status: "active",
                serie: item.productSeries ?? null,
                segmento: item.segmentName ?? null,
                utilizacao: "Aluno",
                quantidade_contratual: item.quantityOrdered,
                desconto: item.contractualDiscount,
                adjustment: 0,
                quantidade_pedido: item.quantityOrdered,
                valor_liquido: item.netPrice,
                valor_total: item.itemTotal,
                delivery_type_id: 2,
                delivery_type_number: 2,
                brand_id: 0,
                product_type: 0,
                is_printed_product: true,
                product_series: item.productSeries ?? null,
                segment_name: item.segmentName ?? null,
                brand_name: item.brandName ?? null,
                product_school_year: item.productSchoolYear ?? null,
                product_sku: item.productSku ?? null,
                quantity_contracted: item.quantityOrdered,
              }));
            sortedData.splice(0, sortedData.length, ...(simulatedRows as unknown as typeof sortedData));
          }

          const orderClient = orderClientResponse.data || [];
          const scheduledAll = scheduledAllResponse.data || [];
          const scheduledCycleCode = selectedCycleYear
            ? buildCycleCode(selectedCycleYear, "PROGRAMADO")
            : null;
          const isScheduledfilter = scheduledAll
            .filter(e => e.clientCode === selectedClient.codigo_cnx)
            .filter(item => scheduledCycleCode !== null
              && item.cycleCode === scheduledCycleCode
              && (item.status === 'pending_confirmation' || item.status === 'confirmed' || item.status === 'pending' || item.status === 'processed'));

          const isScheduled = (scheduledOrderResponse) ? scheduledOrderResponse : isScheduledfilter.length > 0
            ? isScheduledfilter.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
            : null;

          if (!!isScheduled && isScheduled.clientCode && isScheduled.orderPayload) {
            try {
              const payload = typeof isScheduled.orderPayload === 'string'
                ? JSON.parse(isScheduled.orderPayload)
                : isScheduled.orderPayload;

              const dataLastScheduled: ProgramadoDto = payload;
              // Só seta se estiver pendente ou confirmado, conforme regra de negócio
              if (isScheduled.status === 'pending_confirmation' || isScheduled.status === 'confirmed') setLastScheduled(dataLastScheduled);

              // Se for pedido programado, carregar quantidades do payload
              if (typePedido === "PROGRAMADO" && dataLastScheduled && dataLastScheduled.Items) {
                setScheduledUserEmail(dataLastScheduled.CreatedBy || null);
                sortedData.forEach(row => {
                  // Find matching item in payload - case-insensitive code match
                  const scheduledItem = dataLastScheduled.Items.find(e =>
                    String(e.ProductCode || (e as any).productCode || "").toLowerCase() === String(row.codigo_produto || "").toLowerCase()
                  );

                  if (String(row.descricao_produto || "").toLowerCase().includes("2º semestre") && !!scheduledItem) {
                    // Recalcular valores baseados no payload agendado
                    row.quantidade_pedido = scheduledItem.QuantityOrdered || (scheduledItem as any).quantityOrdered || 0;

                    // Map quantity contracted from payload to the row's reference quantity
                    const payloadContracted = scheduledItem.QuantityContracted || (scheduledItem as any).quantityContracted;
                    if (payloadContracted !== undefined) {
                      row.quantidade_contratual = payloadContracted;
                    }

                    row.valor_liquido = Math.round(row.preco * (1 - row.desconto / 100) * 100) / 100;
                    row.valor_total = Math.round(row.valor_liquido * row.quantidade_pedido * 100) / 100;
                  }
                });
              }
            } catch (e) {
              console.error("Erro ao processar payload do pedido agendado:", e);
            }
          }

          // A partir de 2027, scheduled_orders_items é a fonte autoritativa da edição.
          // O payload permanece apenas como contrato de compatibilidade para ciclos legados.
          if (typePedido === "PROGRAMADO" && authoritativeItemsResponse?.data && !isSimulatedEditOrder) {
            // A grade também é usada na criação e pode chegar preenchida com quantidades do
            // perfil da escola. Em edição, nenhum desses valores pode vazar para o payload:
            // somente a composição já existente no pedido pode ser alterada.
            sortedData.forEach((row) => {
              row.quantidade_pedido = 0;
              row.valor_total = 0;
            });

            const normalizeKey = (value: unknown) => String(value ?? "").trim().toLowerCase();
            type AuthoritativeMatch = { item: ScheduledOrderItemTrace; quantity: number };
            const bySku = new Map<string, AuthoritativeMatch>();
            const byCode = new Map<string, AuthoritativeMatch>();
            const byId = new Map<string, AuthoritativeMatch>();
            const addMatch = (
              map: Map<string, AuthoritativeMatch>,
              key: string,
              item: ScheduledOrderItemTrace,
            ) => {
              const current = map.get(key);
              map.set(key, {
                item: current?.item ?? item,
                quantity: (current?.quantity ?? 0) + item.quantityOrdered,
              });
            };
            authoritativeItemsResponse.data
              .filter(item => item.quantityOrdered > 0)
              .forEach((item) => {
                if (item.productSku) addMatch(bySku, normalizeKey(item.productSku), item);
                if (item.productCode) addMatch(byCode, normalizeKey(item.productCode), item);
                if (item.productId !== null && item.productId !== undefined) {
                  addMatch(byId, normalizeKey(item.productId), item);
                }
              });

            sortedData.forEach((row) => {
              const matchedByIdentity = bySku.get(normalizeKey(row.product_sku || row.sku))
                ?? byCode.get(normalizeKey(row.codigo_produto));
              const authoritativeMatch = matchedByIdentity
                ?? byId.get(normalizeKey(row.product_id || row.id));
              if (authoritativeMatch) {
                const authoritativeItem = authoritativeMatch.item;
                // productId é apenas fallback para registros legados. Nesse caso, os
                // identificadores enviados precisam continuar sendo os do snapshot,
                // nunca os de uma variante atual do catálogo.
                row.codigo_produto = authoritativeItem.productCode;
                row.product_sku = authoritativeItem.productSku || row.product_sku;
                row.sku = authoritativeItem.productSku || row.sku;
                row.descricao_produto = authoritativeItem.productDescription || row.descricao_produto;
                row.quantidade_pedido = authoritativeMatch.quantity;
                row.valor_liquido = Math.round(row.preco * (1 - row.desconto / 100) * 100) / 100;
                row.valor_total = Math.round(
                  row.valor_liquido * authoritativeMatch.quantity * 100,
                ) / 100;
              }
            });

            // Em edição, a grade deixa de ser um catálogo: ela contém exclusivamente
            // a composição ativa do pedido. Isso também impede que regras de série,
            // perfil ou produtos relacionados incluam outro SKU no payload.
            const authoritativeRows = sortedData.filter(row => row.quantidade_pedido > 0);
            sortedData.splice(0, sortedData.length, ...authoritativeRows);
          }
        }

        /* Pegando pedidos simulados */
        if (typePedido === "SIMULADO") {
          const simulatedResponse = await simulatedOrdersApi.getAll();
          if (isStale()) return;
          const allCampaigns = simulatedResponse.data || [];
          const now = new Date();
          const active = allCampaigns.filter(c => new Date(c.deadline) >= now);
          setActiveSimulatedCampaigns(active);

          if (active.length > 0) {
            const simulatedProductIds = new Set(active.flatMap(c => c.products.map((p: any) => p.id)));

            sortedData.forEach(row => {
              if (simulatedProductIds.has(row.product_id)) {
                row.quantidade_pedido = row.quantidade_contratual;
                // Simulados sempre têm valor zero (complementares)
                row.preco = 0;
                row.valor_liquido = 0;
                row.valor_total = 0;
              }
            });
          }
        }

        // console.log( "Final order items:", sortedData );
        if (isStale()) return;
        setOrderItems(applyProductBonusesForOrder(sortedData));
      } catch (error) {
        if (isStale()) return;
        if (!handleCycleError(error)) {
          console.error("API error fetching order items:", error);
          toast.error('Erro inesperado!', {
            description: "Ocorreu um erro inesperado. Por favor, tente novamente.",
          });
        }
        setOrderItems([]);
      } finally {
        if (!isStale()) setProductsLoading(false);
      }
    } else {
      setOrderItems([]);
    }
  };

  useEffect(() => {
    fetchOrderItems();
  }, [selectedClient, typePedido, selectedCycleYear]);

  // Ao abrir um Pedido Programado específico para edição, o ciclo selecionado precisa ser
  // o do próprio pedido (cycleCode), não o ciclo "atual" auto-selecionado — senão catálogo,
  // preços e o prazo de ajuste do 2º semestre resolvem para o ciclo errado quando um novo
  // ciclo já abriu e o pedido sendo editado ainda é do ciclo anterior.
  useEffect(() => {
    if (!scheduledOrderId || typePedido !== 'PROGRAMADO') return;
    let cancelled = false;
    scheduledOrdersApi.getById(scheduledOrderId).then((order) => {
      if (cancelled) return;
      const orderCycleYear = resolveCycleYearFromCode(order?.cycleCode);
      if (orderCycleYear && orderCycleYear !== selectedCycleYear) {
        selectCycle(orderCycleYear);
      }
    }).catch(() => { });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledOrderId, typePedido]);


  const itemsToDisplay = useMemo(() => {
    if (typePedido === 'NORMAL') return orderItems;
    if (typePedido === 'PROGRAMADO') {
      // Na edição 2027+, orderItems já foi reduzido aos itens autoritativos do
      // pedido. Reclassificá-los pelo perfil/catálogo descartaria itens válidos
      // cuja classificação cadastral diverge do snapshot.
      if (scheduledOrderId && isNewScheduledOrderCycleEnabledForCycle(selectedCycleYear)) {
        return orderItems;
      }
      return orderItems.filter(item => isProgrammedOrderItem(item, selectedCycleYear));
    }
    if (typePedido === 'SIMULADO') {
      if (activeSimulatedCampaigns.length === 0) return [];
      const productIds = new Set(activeSimulatedCampaigns.flatMap(c => c.products.map((p: any) => p.id)));
      return orderItems.filter(item => productIds.has(item.product_id));
    }
    return orderItems;
  }, [orderItems, typePedido, activeSimulatedCampaigns, scheduledOrderId, selectedCycleYear]);

  const applyProductBonusesForOrder = (items: OrderItem[]) =>
    applyProductBonuses(items, isProductBonusEnabledForCycle(selectedCycleYear ?? availableOrderCycleYears[0]));

  const updateItemQuantity = (item: OrderItem, quantidade: number) => {
    const normalizedQuantity = Math.max(0, quantidade);
    const updatedItem = {
      ...item,
      quantidade_pedido: normalizedQuantity,
    };

    if (typePedido === "SIMULADO") {
      updatedItem.preco = 0;
      updatedItem.valor_liquido = 0;
      updatedItem.valor_total = 0;
    } else {
      updatedItem.valor_liquido =
        Math.round(
          updatedItem.preco *
          (1 - updatedItem.desconto / 100) *
          100
        ) / 100;

      updatedItem.valor_total =
        Math.round(
          updatedItem.valor_liquido *
          updatedItem.quantidade_pedido *
          100
        ) / 100;
    }

    return updatedItem;
  };

  const updateQuantidade = (index: number, quantidade: number) => {
    if (isReadOnlyCycle) return;
    const itemToUpdate = itemsToDisplay[index];
    if (!itemToUpdate) return;

    const originalIndex = orderItems.findIndex(item => item.id === itemToUpdate.id);
    if (originalIndex === -1) return;

    const updatedItem = updateItemQuantity(
      orderItems[originalIndex],
      quantidade
    );

    const newOrderItems = [...orderItems];
    newOrderItems[originalIndex] = updatedItem;

    setOrderItems(applyProductBonusesForOrder(newOrderItems));
  };

  const updateQuantidadesBatch = (
    updates: Array<{
      id: number;
      quantidade: number;
    }>
  ) => {
    if (isReadOnlyCycle) return;
    const newOrderItems = [...orderItems];

    updates.forEach(update => {
      const itemIndex = newOrderItems.findIndex(
        item => item.id === update.id
      );

      if (itemIndex === -1) {
        return;
      }

      const currentItem = orderItems[itemIndex];

      const nextQuantity = Math.max(0, update.quantidade);
      newOrderItems[itemIndex] = updateItemQuantity(
        currentItem,
        nextQuantity
      );
    });

    setOrderItems(applyProductBonusesForOrder(newOrderItems));
  };

  const getGrossSubtotal = () => {
    // Calculate total for annual products (delivery_type_number = 1), excluding 2º semestre
    return orderItems
      .filter(
        (item) =>
          item.delivery_type_number === 1 &&
          item.quantidade_pedido > 0
        // && !item.descricao_produto.includes( "2º Semestre" )
      )
      .reduce((total, item) => total + item.preco * item.quantidade_pedido, 0);
  };

  const getSubtotal = () => {
    return itemsToDisplay
      .filter((item) => item.delivery_type_number === 2 && item.quantidade_pedido > 0)
      .reduce((total, item) => total + item.valor_total, 0);
  };

  const getTotal = () => {
    return itemsToDisplay
      .filter((item) => item.quantidade_pedido > 0)
      .filter((item) => !(typePedido === "NORMAL"
        && isNewScheduledOrderCycleEnabledForCycle(selectedCycleYear)
        && isDeferredProgrammedOrderItem(item, selectedCycleYear)))
      // .filter( ( item ) => typePedido === 'NORMAL' ? !item.descricao_produto.includes( "2º Semestre" ) : item.descricao_produto.includes( "2º Semestre" ) )
      .reduce((total, item) => total + item.valor_total, 0);
  };


  // Get shipping totals from the centralized hook
  const { freteAnual, freteSemestral } = useOrderCalculations(
    orderItems,
    isScheduled,
    (selectedClient as any)?.shipping_fee || 0,
    selectedCycleYear ?? undefined,
  );

  const validateSeriesConsistency = () => {
    const processarProdutos = () => {
      // 1. Filtrar somente "Aluno"
      let alunos: OrderItem[];
      if (typePedido === 'NORMAL') {
        alunos = orderItems.filter(p => p.utilizacao === "Aluno" && !isDeferredProgrammedOrderItem(p, selectedCycleYear))
      } else {
        alunos = orderItems.filter(p => p.utilizacao === "Aluno" && isProgrammedOrderItem(p, selectedCycleYear))
      }

      // 2. Agrupar por série
      const agrupado = alunos.reduce((acc, item) => {
        if (!acc[item.serie]) acc[item.serie] = [];
        acc[item.serie].push(item);
        return acc;
      }, {} as Record<string, OrderItem[]>);

      // 3. Em cada série pegar somente itens com quantidade_pedido diferente
      const resultado: Record<string, OrderItem[]> = {};

      Object.keys(agrupado).forEach(serie => {
        const itens = agrupado[serie];

        // Se tiver só um item, não faz validação
        if (itens.length <= 1) {
          resultado[serie] = [];
          return;
        }

        // conta quantas vezes cada quantidade_pedido aparece
        const mapa = itens.reduce((acc, item) => {
          acc[item.quantidade_pedido] = (acc[item.quantidade_pedido] || 0) + 1;
          return acc;
        }, {} as Record<number, number>);

        // pega somente quantidades que aparecem 1 vez
        const quantidadesUnicas = Object.keys(mapa)
          .filter(qtd => mapa[qtd] === 1)
          .map(Number);

        // filtra os itens onde quantidade_pedido é única
        resultado[serie] = itens.filter(i =>
          quantidadesUnicas.includes(i.quantidade_pedido)
        );
      });

      return resultado;
    };

    const produtcsFilter = processarProdutos();
    const toastError: string[] = [];

    Object.keys(produtcsFilter).forEach(item => {
      const serie = produtcsFilter[item];
      if (serie.length) {
        serie.forEach(e => {
          toastError.push(`Identificamos que, no segmento ${mapEtapaToSegment(e.serie)} (${mapEtapaToGradeName(e.serie)}), o produto “${e.descricao_produto}” apresenta uma quantidade de pedidos diferente dos outros itens do mesmo grupo.
            Para corrigir, acesse o segmento desse produto e atualize a quantidade da série conforme o padrão aplicado aos demais produtos.` );
        });
      }
    });

    return toastError;
  };

  const validateSeriesConsistencySemetre = () => {
    if (typePedido !== 'NORMAL') return [];

    const toastError: string[] = [];

    // 1. Agrupar todos os produtos de "Aluno" por série
    const agrupadoPorSerie = orderItems.reduce((acc, item) => {
      if (item.utilizacao === "Aluno") {
        const serie = item.serie || "sem_serie";
        if (!acc[serie]) acc[serie] = [];
        acc[serie].push(item);
      }
      return acc;
    }, {} as Record<string, OrderItem[]>);

    // 2. Validar cada série
    Object.keys(agrupadoPorSerie).forEach(serie => {
      const itens = agrupadoPorSerie[serie];

      const temPrimeiroSemestreComQtd = itens.some(item =>
        item.descricao_produto.includes("1º Semestre") && item.quantidade_pedido > 0
      );

      // Itens anuais são aqueles que não são nem 1º nem 2º Semestre
      const temAnualComQtd = itens.some(item => !item.descricao_produto.includes("1º Semestre") && item.quantidade_pedido > 0);

      if (temPrimeiroSemestreComQtd && !temAnualComQtd) {
        toastError.push(`Verifique a quantidade solicitada para a série ${mapEtapaToGradeName(serie)}. Os pedidos realizados a partir de Maio de 2026 já contemplam os itens do 2º semestre.`);
      }
    });

    return toastError;
  };



  const finalizarPedido = async (reason?: string, coupon?: CouponValidation | null) => {
    if (isReadOnlyCycle) {
      toast.error('Ciclo fechado', {
        description: `O ciclo ${selectedCycleYear} está disponível apenas para visualização.`
      });
      return;
    }
    await processFinalize(reason, coupon);
  };

  const processFinalize = async (backofficeReason?: string, appliedCoupon?: CouponValidation | null) => {
    statusModalControls.setError(false);
    // Validações básicas
    if (!selectedClient) {
      toast.error('Erro!', {
        description: "Nenhum cliente selecionado."
      });
      return;
    }

    if (orderItems.length === 0) {
      toast.error('Erro!', {
        description: "Nenhum item no pedido."
      });
      return;
    }

    const itemsWithQuantity = orderItems.filter((item) => item.quantidade_pedido > 0);

    if (itemsWithQuantity.length === 0) {
      toast.error("Nenhum item com quantidade maior que zero.");
      return;
    }

    /*
    const toastError = validateSeriesConsistencySemetre();

    if (toastError.length) {
      toastError.forEach(row => {
        toast.warning('Atenção:', {
          description: <div style={{ whiteSpace: "pre-line" }}> {row} </div>,
          position: 'top-center'
        });
      });
      return;
    }
    */

    // Use the separated freights
    const totalShippingAmount = freteAnual + freteSemestral;

    let couponReservationId: string | null = null;
    const couponOrderReferences: CouponOrderReference[] = [];



    try {
      statusModalControls.showValidating();
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const isNormal = typePedido === "NORMAL";
      const isProgramado = typePedido === "PROGRAMADO";
      const isSimulado = typePedido === "SIMULADO";

      // Ano do ciclo comercial: o ciclo selecionado pelo usuário (usado para buscar o
      // catálogo/perfil atual) é sempre a fonte de verdade; ano_letivo do produto é só
      // fallback para os fluxos legados em que nenhum ciclo foi selecionado.
      const orderYear = resolveOrderCycleYear(itemsWithQuantity, selectedCycleYear);
      const deferSecondSemesterCharge = isNormal
        && isNewScheduledOrderCycleEnabledForCycle(orderYear);
      // Cópia só de apresentação do item programado (valor zerado); o preço vigente fica no item original.
      const toProgrammedPreview = (item: OrderItem): OrderItem => ({
        ...item,
        descricao_produto: `${item.descricao_produto} — Produto programado (cobrança na confirmação)`,
        valor_liquido: 0,
        valor_total: 0,
      });
      const itemsForImmediateDocument = deferSecondSemesterCharge
        ? itemsWithQuantity.flatMap((item): OrderItem[] => {
          // Avaliações Nacionais / Simulado ENEM: um item faturado agora (com valor) e outro programado
          // (valor zerado), com a mesma quantidade. A cópia nunca é enviada ao backend, que gera o programado.
          if (item.scheduling_category === "ImmediateAndExam") {
            return [item, { ...toProgrammedPreview(item), preco: 0 }];
          }
          return isProgrammedOrderItem(item, orderYear) ? [toProgrammedPreview(item)] : [item];
        })
        : itemsWithQuantity;
      const chargeableItems = deferSecondSemesterCharge
        ? itemsWithQuantity.filter(item => !isDeferredProgrammedOrderItem(item, orderYear))
        : itemsWithQuantity;
      const couponItemKeys = new Map<OrderItem, string>();
      // Cupom: os itens faturados agora (anual/semestral) e, para o cupom por item, os de 2º semestre programado
      // (o desconto deles fica reservado e é abatido na cobrança do pedido programado). Avaliações não entram.
      const couponScheduledItems = deferSecondSemesterCharge
        ? itemsWithQuantity.filter(item => isDeferredProgrammedOrderItem(item, orderYear)
          && item.scheduling_category !== "ExamScheduled" && (item.valor_total || 0) > 0)
        : [];
      const couponEligibleItems = [...chargeableItems, ...couponScheduledItems];
      couponEligibleItems.forEach((item, index) => {
        couponItemKeys.set(item, `${item.product_id || item.id}-${item.codigo_produto}-${index}`);
      });

      let reservedCoupon: CouponValidation | null = null;
      if (appliedCoupon && !isNormal) {
        // Cupom só vale para o que é faturado na hora (pedido imediato anual/semestral).
        throw new Error("O cupom só pode ser aplicado em pedidos faturados na hora.");
      }
      if (appliedCoupon) {
        const reservation = await couponApi.reserve(
          appliedCoupon.code,
          couponEligibleItems.map(item => ({
            itemKey: couponItemKeys.get(item)!,
            bucket: couponBucketForItem(item, orderYear, deferSecondSemesterCharge),
            productId: item.product_id || item.id,
            productCode: item.codigo_produto,
            productsSubtotal: item.valor_total,
            quantity: item.quantidade_pedido,
            bonifiedQuantity: item.bonified_quantity ?? 0,
          })),
          selectedClient.codigo_cnx,
          orderYear,
        );
        couponReservationId = reservation.reservationId;
        reservedCoupon = reservation.coupon;
      }

      const couponDiscountForItems = (items: OrderItem[]) => {
        if (!reservedCoupon) return 0;
        const keys = new Set(items.map(item => couponItemKeys.get(item)).filter(Boolean));
        // Só o desconto do faturado agora entra nos pedidos; o de 2º semestre fica reservado para a cobrança do programado.
        const total = reservedCoupon.allocations
          .filter(allocation => keys.has(allocation.itemKey) && (!allocation.bucket || allocation.bucket === "Annual" || allocation.bucket === "Semestral"))
          .reduce((sum, allocation) => sum + allocation.discountAmount, 0);
        return Math.round(total * 100) / 100;
      };
      // Cupom por itens: unidades bonificadas por pedido (PDF informativo e, com a flag, desconto no valor do item).
      const couponPdfItemsFor = (items: OrderItem[]): PdfCouponItem[] => {
        if (!reservedCoupon) return [];
        const keyOwner = (itemKey: string) => items.find(item => couponItemKeys.get(item) === itemKey);
        // Cupom do pedido todo (sem itens): o desconto de cada item vai para o PDF sem unidades cobertas, e todos os
        // valores do PDF (itens, resumo por segmento e total do extrato) saem já descontados.
        if (!hasCouponUnits(reservedCoupon)) {
          return reservedCoupon.allocations
            .filter(allocation => allocation.discountAmount > 0 && !!keyOwner(allocation.itemKey))
            .map(allocation => {
              const owner = keyOwner(allocation.itemKey)!;
              return {
                productCode: owner.codigo_produto,
                description: owner.descricao_produto,
                quantity: 0,
                discountAmount: allocation.discountAmount,
              };
            });
        }
        return buildCouponBonifiedLines(
          reservedCoupon.allocations.filter(allocation => !!keyOwner(allocation.itemKey)),
          allocation => keyOwner(allocation.itemKey)?.descricao_produto,
        ).map(({ productCode, description, quantity, discountAmount }) => ({ productCode, description, quantity, discountAmount }));
      };
      // Cupom com parte programada (2º semestre): todos os pedidos criados ficam vinculados ao uso do cupom, mesmo sem
      // desconto imediato, para o abatimento na cobrança do programado encontrar o cupom.
      const couponHasScheduled = !!reservedCoupon
        && ((reservedCoupon.scheduledSecondSemesterDiscountAmount ?? 0) + (reservedCoupon.scheduledExamDiscountAmount ?? 0)) > 0;
      // O backend recalcula o desconto a partir da reserva: aqui só vai o vínculo (reserva + itens do pedido),
      // e total_amount segue bruto (sem desconto), sem tocar no frete.
      const couponPayloadForItems = (items: OrderItem[]) => {
        if (!couponReservationId || (couponDiscountForItems(items) <= 0 && !couponHasScheduled)) return {};
        return {
          coupon_reservation_id: couponReservationId,
          coupon_items: items
            .filter(item => couponItemKeys.has(item))
            .map(item => ({
              productId: item.product_id || item.id,
              productCode: item.codigo_produto,
              itemTotal: item.valor_total || 0,
              quantityOrdered: item.quantidade_pedido || 0,
            })),
        };
      };

      let cycleCode = '';
      if (isNormal) cycleCode = buildCycleCode(orderYear, "NORMAL");
      else if (isProgramado) cycleCode = editedSimulado?.cycleCode ?? buildCycleCode(orderYear, "PROGRAMADO");
      else if (isSimulado) cycleCode = buildCycleCode(orderYear, "SIMULADO");

      statusModalControls.showProcessing(10);

      let lastUsedTimestamp = 0;
      const getNextOrderNumber = (prefix: string) => {
        let now = new Date().getTime();
        if (now <= lastUsedTimestamp) {
          now = lastUsedTimestamp + 1;
        }
        lastUsedTimestamp = now;
        return `${prefix}-${now}-${selectedClient.codigo_cnx}`;
      };

      // Helper para mapear itens para o formato da API
      const mapOrderItems = (items: OrderItem[]) => items.map((item) => ({
        product_id: item.product_id || item.id,
        codigo_produto: item.codigo_produto,
        descricao_produto: item.descricao_produto,
        preco: item.preco,
        quantidade: item.quantidade_pedido,
        desconto: item.desconto,
        valor_total: item.valor_total,
        delivery_type_id: item.delivery_type_id,
        brand_id: item.brand_id,
        product_series: item.product_series || item.serie || null,
        segment_name: item.segment_name || item.segmento || null,
        brand_name: item.brand_name || item.marca || null,
        product_school_year: item.product_school_year || item.ano_letivo || null,
        product_sku: item.product_sku || item.sku || null,
        quantity_contracted: item.quantity_contracted || item.quantidade_contratual || 0,
        bonified_quantity: item.bonified_quantity,
      }));

      let annualOrderNumber = null;
      let semestralOrderNumber = null;
      let annualOrderId: string | null = null;
      let semestralOrderId: string | null = null;
      let generatedOrderNumber = "";
      let sourcePurchaseOrderId: string | null = null;
      let pendingScheduledOrderData: any = null;

      // --- FLUXO PEDIDO NORMAL ---
      if (isNormal) {
        // Compatibilidade exclusiva dos ciclos legados. Nos ciclos novos esta decisão
        // pertence ao backend e usa exams_sku_composition, não a descrição.
        const isLegacySimuladoDescricao = (descricao: string) => {
          const desc = descricao.toLowerCase();
          return desc.includes('avaliações nacionais') || desc.includes('simulado enem');
        };

        const secondSemesterItems = itemsWithQuantity.filter((item) => item.descricao_produto.includes("2º Semestre"));
        const usesAuthoritativeScheduledOrder = isNewScheduledOrderCycleEnabledForCycle(orderYear);

        // Itens de 2º semestre só são tratados no fluxo "programado" separado enquanto o prazo
        // de ajuste não passou. Depois do prazo, não faz sentido mais "programar" o pedido: eles
        // devem seguir no processamento normal (anual/semestral) para não serem perdidos.
        const date = new Date();
        const endSecondSemester = getSecondSemesterAdjustmentDeadline(orderYear);
        const canScheduleSecondSemester = secondSemesterItems.length > 0
          && (usesAuthoritativeScheduledOrder || endSecondSemester > date);

        // Nos ciclos novos o backend classifica, pelo catálogo e pelos de-paras oficiais,
        // quais itens são imediatos, de segundo semestre ou de avaliações/simulados.
        const itemsForNormalProcessing = itemsWithQuantity.filter(item =>
          (usesAuthoritativeScheduledOrder
            || !canScheduleSecondSemester
            || !item.descricao_produto.includes("2º Semestre"))
          && (usesAuthoritativeScheduledOrder || !isLegacySimuladoDescricao(item.descricao_produto))
        );

        const annualItems = itemsForNormalProcessing.filter((item) => item.delivery_type_number === 1);
        // Qualquer item que não seja anual (delivery_type_number 2, 3, ou não configurado)
        // entra como semestral, para nenhum item ficar "órfão" sem gerar pedido algum.
        const semestralItems = itemsForNormalProcessing.filter((item) => item.delivery_type_number !== 1);

        // Se houver itens de 2º semestre em um pedido NORMAL, lidar com eles
        if (canScheduleSecondSemester && !usesAuthoritativeScheduledOrder) {
          console.log('Iniciado procesamento para o itens de 2º semestre em um pedido Imediato/Complementar');

          let orderNumberProgramado = getNextOrderNumber("PED-S");
          let finalItemsForScheduled = secondSemesterItems.map(item => ({
            productCode: item.codigo_produto,
            productSku: item.sku,
            productDescription: item.descricao_produto,
            productSeries: `${item.serie}`,
            educationLevel: `${item.ano_letivo}`,
            brand: `${item.brand_name || item.marca}`,
            academicYear: `${item.ano_letivo}`,
            unitPrice: item.preco,
            contractualDiscount: item.desconto,
            discountAmount: item.preco - item.valor_liquido,
            netPrice: item.valor_liquido,
            quantityContracted: item.quantidade_contratual,
            quantityOrdered: item.quantidade_pedido,
            bonifiedQuantity: item.bonified_quantity ?? 0,
            itemTotal: item.valor_total
          }));

          // Se já existe um pedido programado PENDENTE, somar os itens
          if (lastScheduled && !usesAuthoritativeScheduledOrder) {
            orderNumberProgramado = lastScheduled.OrderNumber;
            const existingItems = lastScheduled.Items || [];

            // Criar um Map para facilitar a soma
            const itemsMap = new Map();
            existingItems.forEach((item: any) => {
              const code = item.ProductCode || item.productCode;
              if (code) itemsMap.set(code, { ...item });
            });

            finalItemsForScheduled.forEach(newItem => {
              const code = newItem.productCode;
              if (itemsMap.has(code)) {
                const existing = itemsMap.get(code);
                const unitPrice = existing.UnitPrice || existing.unitPrice || 0;
                const discount = existing.ContractualDiscount || existing.contractualDiscount || 0;
                const existingQty = existing.QuantityOrdered || existing.quantityOrdered || 0;

                const newQty = existingQty + newItem.quantityOrdered;

                // Atualizar preservando o estilo de caixa original se possível
                if (existing.QuantityOrdered !== undefined) {
                  existing.QuantityOrdered = newQty;
                  existing.ItemTotal = Math.round(unitPrice * (1 - discount / 100) * newQty * 100) / 100;
                } else {
                  existing.quantityOrdered = newQty;
                  existing.itemTotal = Math.round(unitPrice * (1 - discount / 100) * newQty * 100) / 100;
                }
              } else {
                // Se não existe, adiciona ao Map (convertendo para o formato PascalCase do payload)
                itemsMap.set(code, {
                  ProductCode: newItem.productCode,
                  ProductSku: newItem.productSku,
                  ProductDescription: newItem.productDescription,
                  ProductSeries: newItem.productSeries,
                  EducationLevel: newItem.educationLevel,
                  Brand: newItem.brand,
                  AcademicYear: newItem.academicYear,
                  UnitPrice: newItem.unitPrice,
                  ContractualDiscount: newItem.contractualDiscount,
                  DiscountAmount: newItem.discountAmount,
                  NetPrice: newItem.netPrice,
                  QuantityContracted: newItem.quantityContracted,
                  QuantityOrdered: newItem.quantityOrdered,
                  BonifiedQuantity: newItem.bonifiedQuantity,
                  ItemTotal: newItem.itemTotal
                });
              }
            });

            finalItemsForScheduled = Array.from(itemsMap.values());
          }

          const totalProg = usesAuthoritativeScheduledOrder ? 0 : finalItemsForScheduled.reduce((sum, item: any) => sum + (item.ItemTotal || item.itemTotal || 0), 0);
          const subtotalProg = usesAuthoritativeScheduledOrder ? 0 : finalItemsForScheduled.reduce((sum, item: any) => sum + ((item.NetPrice || item.netPrice || 0) * (item.QuantityOrdered || item.quantityOrdered || 0)), 0);
          const totalQuantityProg = usesAuthoritativeScheduledOrder ? 0 : finalItemsForScheduled.reduce((sum, item: any) => sum + (item.QuantityOrdered || item.quantityOrdered || 0), 0);

          const scheduledOrderData = {
            order_number: orderNumberProgramado,
            client_name: selectedClient.nome_cliente,
            created_by: scheduledUserEmail || user.email,
            client_code: selectedClient.codigo_cnx,
            total_amount: totalProg || 0,
            shipping_amount: 0,
            subtotal_amount: subtotalProg || 0,
            total_items: totalQuantityProg || 0,
            status: "pending_confirmation",
            client_company_name: selectedClient.nome_cliente,
            client_cnpj: selectedClient.cnpj || null,
            client_address: selectedClient.endereco || null,
            client_state: selectedClient.state || selectedClient.estado || null,
            client_city: selectedClient.city || selectedClient.cidade || null,
            client_phone: selectedClient.phone || selectedClient.telefone || null,
            client_email: getUserEmailSync() || null,
            shipping_address_id: shippingAddressId,
            deliveryTypeId: 2,
            isScheduled: false, // Pedido normal não é programado conforme solicitado
            cycle_code: buildCycleCode(orderYear, "PROGRAMADO"),
            quantityInstallment: selectedSemestralInstallments,
            paymentConfiguration: selectedSemestralCondition,
            customer: {
              clientCodeCnx: selectedClient.codigo_cnx,
              clientCompanyName: selectedClient.nome_cliente
            },
            items: finalItemsForScheduled.map(item => ({
              productCode: item.ProductCode || item.productCode,
              productSku: item.ProductSku || item.productSku,
              productDescription: item.ProductDescription || item.productDescription,
              productSeries: item.ProductSeries || item.productSeries,
              educationLevel: item.EducationLevel || item.educationLevel,
              brand: item.Brand || item.brand,
              academicYear: item.AcademicYear || item.academicYear,
              unitPrice: item.UnitPrice || item.unitPrice,
              contractualDiscount: item.ContractualDiscount || item.contractualDiscount,
              discountAmount: item.DiscountAmount || item.discountAmount,
              netPrice: item.NetPrice || item.netPrice,
              quantityContracted: item.QuantityContracted || item.quantityContracted,
              quantityOrdered: item.QuantityOrdered || item.quantityOrdered,
              bonifiedQuantity: item.BonifiedQuantity ?? item.bonifiedQuantity ?? 0,
              itemTotal: item.ItemTotal || item.itemTotal
            }))
          };

          // Persistir somente depois do pedido Realizado de Contrato, para que a origem aponte
          // para um purchase_orders que já exista.
          pendingScheduledOrderData = scheduledOrderData;
          semestralOrderNumber = orderNumberProgramado;
        }

        if (annualItems.length > 0) {
          annualOrderNumber = getNextOrderNumber("PED-A");
          const annualCouponDiscount = couponDiscountForItems(annualItems);
          const annualCouponPayload = couponPayloadForItems(annualItems);
          const annualOrderData = {
            order_number: annualOrderNumber,
            client_name: selectedClient.nome_cliente,
            client_code: selectedClient.codigo_cnx,
            total_amount: annualItems.reduce((sum, item) => sum + (item.valor_total || 0), 0),
            created_by: user.email,
            shipping_amount: freteAnual || 0,
            subtotal_amount: annualItems.reduce((sum, item) => sum + ((item.valor_liquido || 0) * (item.quantidade_pedido || 0)), 0),
            total_items: annualItems.reduce((sum, item) => sum + (item.quantidade_pedido || 0), 0),
            status: "pending",
            client_company_name: selectedClient.nome_cliente,
            client_cnpj: selectedClient.cnpj || null,
            client_address: selectedClient.endereco || null,
            client_state: selectedClient.estado || null,
            client_city: selectedClient.cidade || null,
            client_phone: selectedClient.telefone || null,
            client_email: getUserEmailSync() || null,
            shipping_address_id: shippingAddressId,
            deliveryTypeId: 1,
            isScheduled: false,
            cycle_code: cycleCode,
            quantityInstallment: selectedAnnualInstallments,
            paymentConfiguration: selectedAnnualCondition,
            ...annualCouponPayload,
          };

          const response: any = await ordersApi.create(annualOrderData);
          const orderId = response.order_id || response.data?.order_id;
          await ordersApi.createItemsBatch({ order_id: orderId, items: mapOrderItems(annualItems) });
          annualOrderId = orderId;
          if (annualCouponDiscount > 0 || couponHasScheduled) couponOrderReferences.push({ id: orderId, orderNumber: annualOrderNumber, discountAmount: annualCouponDiscount });
          sourcePurchaseOrderId ??= orderId;
        }

        if (semestralItems.length > 0) {
          semestralOrderNumber = getNextOrderNumber("PED-S");
          const semestralCouponDiscount = couponDiscountForItems(semestralItems);
          const semestralCouponPayload = couponPayloadForItems(semestralItems);
          const semestralOrderData = {
            order_number: semestralOrderNumber,
            client_name: selectedClient.nome_cliente,
            client_code: selectedClient.codigo_cnx,
            total_amount: semestralItems.reduce((sum, item) => sum + (item.valor_total || 0), 0),
            created_by: user.email,
            shipping_amount: freteSemestral || 0,
            subtotal_amount: semestralItems.reduce((sum, item) => sum + ((item.valor_liquido || 0) * (item.quantidade_pedido || 0)), 0),
            total_items: semestralItems.reduce((sum, item) => sum + (item.quantidade_pedido || 0), 0),
            status: "pending",
            client_company_name: selectedClient.nome_cliente,
            client_cnpj: selectedClient.cnpj || null,
            client_address: selectedClient.endereco || null,
            client_state: selectedClient.estado || null,
            client_city: selectedClient.cidade || null,
            client_phone: selectedClient.telefone || null,
            client_email: getUserEmailSync() || null,
            shipping_address_id: shippingAddressId,
            deliveryTypeId: 2,
            isScheduled: false,
            cycle_code: cycleCode,
            quantityInstallment: selectedSemestralInstallments,
            paymentConfiguration: selectedSemestralCondition,
            ...semestralCouponPayload,
          };

          const response: any = await ordersApi.create(semestralOrderData);
          const orderId = response.order_id || response.data?.order_id;
          await ordersApi.createItemsBatch({ order_id: orderId, items: mapOrderItems(semestralItems) });
          semestralOrderId = orderId;
          if (semestralCouponDiscount > 0 || couponHasScheduled) couponOrderReferences.push({ id: orderId, orderNumber: semestralOrderNumber, discountAmount: semestralCouponDiscount });
          sourcePurchaseOrderId ??= orderId;
        }

        if (pendingScheduledOrderData) {
          await ordersApi.create({
            ...pendingScheduledOrderData,
            sourcePurchaseOrderId: sourcePurchaseOrderId || undefined,
          });
        }

        // Fluxo legado: ciclos anteriores ao motor autoritativo ainda dependem da
        // classificação histórica do frontend.
        if (!usesAuthoritativeScheduledOrder) {
          const simulados = itemsWithQuantity.filter(item =>
            isLegacySimuladoDescricao(item.descricao_produto)
          );
          if (simulados.length > 0) {
            semestralOrderNumber = getNextOrderNumber("PED-SIM");
            await ordersApi.create({
              order_number: semestralOrderNumber,
              client_name: selectedClient.nome_cliente,
              client_code: selectedClient.codigo_cnx,
              total_amount: simulados.reduce((sum, item) => sum + (item.valor_total || 0), 0),
              created_by: user.email,
              shipping_amount: 0,
              subtotal_amount: simulados.reduce((sum, item) => sum + ((item.valor_liquido || 0) * (item.quantidade_pedido || 0)), 0),
              total_items: simulados.reduce((sum, item) => sum + (item.quantidade_pedido || 0), 0),
              status: "pending",
              client_company_name: selectedClient.nome_cliente,
              client_cnpj: selectedClient.cnpj || null,
              client_address: selectedClient.endereco || null,
              client_state: selectedClient.estado || null,
              client_city: selectedClient.cidade || null,
              client_phone: selectedClient.telefone || null,
              client_email: getUserEmailSync() || null,
              shipping_address_id: shippingAddressId,
              deliveryTypeId: 2,
              isScheduled: true,
              cycle_code: buildCycleCode(orderYear, "SIMULADO"),
              quantityInstallment: selectedSemestralInstallments,
              sourcePurchaseOrderId: sourcePurchaseOrderId || undefined,
              items: simulados.map(item => ({
                productCode: item.codigo_produto,
                productSku: item.sku,
                productDescription: item.descricao_produto,
                productSeries: `${item.serie}`,
                educationLevel: `${item.ano_letivo}`,
                brand: `${(item as any).brand_name || item.marca}`,
                academicYear: `${item.ano_letivo}`,
                unitPrice: item.preco || 0,
                contractualDiscount: item.desconto || 0,
                discountAmount: (item.preco || 0) - (item.valor_liquido || 0),
                netPrice: item.valor_liquido || 0,
                quantityContracted: item.quantidade_contratual || 0,
                quantityOrdered: item.quantidade_pedido || 0,
                itemTotal: item.valor_total || 0
              }))
            });
          }
        }

        // Se nenhum pedido foi de fato criado (nenhuma chamada a ordersApi.create acima
        // foi executada), interrompe aqui em vez de simular uma finalização bem-sucedida.
        if (!annualOrderNumber && !semestralOrderNumber) {
          throw new Error("Nenhum item do pedido pôde ser processado. Verifique as quantidades e tente novamente.");
        }

        generatedOrderNumber = annualOrderNumber || semestralOrderNumber || getNextOrderNumber("PED");

      }

      // --- FLUXO PEDIDO PROGRAMADO OU SIMULADO ---
      else if (isProgramado || isSimulado) {
        const prefix: string = isSimulado ? "PED-SIM" : "PED-S";
        let orderNumberProgramado: string = getNextOrderNumber(prefix);
        const finalItemsForScheduled = itemsWithQuantity.map(item => ({
          productCode: item.codigo_produto,
          productSku: item.sku,
          productDescription: item.descricao_produto,
          productSeries: `${item.serie} `,
          educationLevel: `${item.ano_letivo} `,
          brand: `${(item as any).brand_name || item.marca} `,
          academicYear: `${item.ano_letivo} `,
          unitPrice: item.preco || 0,
          contractualDiscount: item.desconto || 0,
          discountAmount: (item.preco || 0) - (item.valor_liquido || 0),
          netPrice: item.valor_liquido || 0,
          quantityContracted: item.quantidade_contratual || 0,
          quantityOrdered: item.quantidade_pedido || 0,
          bonifiedQuantity: item.bonified_quantity ?? 0,
          itemTotal: item.valor_total || 0
        }));

        // Se o pedido já existe e for com status pendente
        if (isProgramado && editedSimulado) {
          orderNumberProgramado = editedSimulado.orderNumber;
        } else if (isProgramado && lastScheduled) {
          orderNumberProgramado = lastScheduled.OrderNumber;
          // Não somamos mais as quantidades aqui, pois no modo PROGRAMADO 
          // a tela já carrega as quantidades do pedido anterior e o usuário as edita.
          // Portanto, usamos o que está na tela como o novo estado final.
        }

        generatedOrderNumber = orderNumberProgramado;

        const total = finalItemsForScheduled.reduce((sum, item: any) => sum + (item.ItemTotal || item.itemTotal || 0), 0);
        const subtotal = finalItemsForScheduled.reduce((sum, item: any) => sum + ((item.NetPrice || item.netPrice || 0) * (item.QuantityOrdered || item.quantityOrdered || 0)), 0);
        const totalQuantity = finalItemsForScheduled.reduce((sum, item: any) => sum + (item.QuantityOrdered || item.quantityOrdered || 0), 0);

        const orderData = {
          order_number: orderNumberProgramado,
          client_name: selectedClient.nome_cliente,
          created_by: user.email,
          client_code: selectedClient.codigo_cnx,
          total_amount: total || 0,
          shipping_amount: 0,
          subtotal_amount: subtotal || 0,
          total_items: totalQuantity || 0,
          status: "pending_confirmation",
          // toBackendOrder converte backofficeReason para o campo Comment do DTO da API.
          backofficeReason: backofficeReason || null,
          client_company_name: selectedClient.nome_cliente,
          client_cnpj: selectedClient.cnpj || null,
          client_address: selectedClient.endereco || null,
          client_state: selectedClient.state || selectedClient.estado || null,
          client_city: selectedClient.city || selectedClient.cidade || null,
          client_phone: selectedClient.phone || selectedClient.telefone || null,
          client_email: getUserEmailSync() || null,
          shipping_address_id: shippingAddressId,
          deliveryTypeId: 2,
          isScheduled: true,
          cycle_code: cycleCode,
          quantityInstallment: selectedSemestralInstallments,
          paymentConfiguration: selectedSemestralCondition,
          // Campos específicos para o formato que a API espera nestes casos
          customer: {
            clientCodeCnx: selectedClient.codigo_cnx,
            clientCompanyName: selectedClient.nome_cliente
          },
          items: finalItemsForScheduled.map(item => ({
            productCode: item.ProductCode || item.productCode,
            productSku: item.ProductSku || item.productSku,
            productDescription: item.ProductDescription || item.productDescription,
            productSeries: item.ProductSeries || item.productSeries,
            educationLevel: item.EducationLevel || item.educationLevel,
            brand: item.Brand || item.brand,
            academicYear: item.AcademicYear || item.academicYear,
            unitPrice: item.UnitPrice || item.unitPrice,
            contractualDiscount: item.ContractualDiscount || item.contractualDiscount,
            discountAmount: item.DiscountAmount || item.discountAmount,
            netPrice: item.NetPrice || item.netPrice,
            quantityContracted: item.QuantityContracted || item.quantityContracted,
            quantityOrdered: item.QuantityOrdered || item.quantityOrdered,
            bonifiedQuantity: item.BonifiedQuantity ?? item.bonifiedQuantity ?? 0,
            itemTotal: item.ItemTotal || item.itemTotal
          }))
        };

        const response: any = await ordersApi.create(orderData);
        const orderId = response.order_id || response.data?.order_id;
        semestralOrderId = orderId;



        if (isProgramado
          && !backofficeReason
          && !isNewScheduledOrderCycleEnabledForCycle(orderYear)) {
          const getPedido = await scheduledOrdersApi.getById(orderId);
          const user = await userManager.getUser();
          const dto: any = getPedido;
          dto.email = user?.profile.email || "";
          await scheduledOrdersApi.changeConfirm(orderId, dto);
        }

        if (isSimulado) {
          semestralOrderNumber = orderNumberProgramado;
        } else {
          semestralOrderNumber = orderNumberProgramado;
        }
      }

      if (couponReservationId) {
        if (couponOrderReferences.length === 0) {
          throw new Error("Nenhum pedido elegível foi criado para registrar o uso do cupom.");
        }
        await couponApi.commit(couponReservationId, couponOrderReferences);
        couponReservationId = null;
      }

      setOrderNumber(generatedOrderNumber || annualOrderNumber || semestralOrderNumber);
      statusModalControls.showProcessing(80);
      await new Promise((resolve) => setTimeout(resolve, 500));

      statusModalControls.showGenerating();
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const completionMessage = [
        annualOrderNumber && `Anual: ${annualOrderNumber} `,
        semestralOrderNumber && `${isSimulado ? "Simulado" : "Semestral"}: ${semestralOrderNumber} `,
      ].filter(Boolean).join(" | ");

      statusModalControls.showCompleted(completionMessage || generatedOrderNumber);
      setCsatOrderNumber(generatedOrderNumber || annualOrderNumber || semestralOrderNumber || "");



      // Salvar dados do pedido completado para possível re-download
      setCompletedOrderData({
        orderNumber: generatedOrderNumber,
        annualOrderNumber: annualOrderNumber || undefined,
        semestralOrderNumber: semestralOrderNumber || undefined,
        annualOrderId: annualOrderId || undefined,
        semestralOrderId: semestralOrderId || undefined,
        clientName: selectedClient.nome_cliente,
        clientCode: selectedClient.codigo_cnx,
        orderItems: itemsForImmediateDocument,
        grossSubtotal: getGrossSubtotal(),
        netSubtotal: getSubtotal(),
        // Só a parte imediata do cupom entra no total; a sobra dos programados é abatida na cobrança deles.
        total: getTotal() + totalShippingAmount - (reservedCoupon?.immediateDiscountAmount ?? reservedCoupon?.discountAmount ?? 0),
        shippingTotal: totalShippingAmount,
        annualShippingTotal: freteAnual,
        semestralShippingTotal: freteSemestral,
        annualCouponDiscount: couponDiscountForItems(itemsForImmediateDocument.filter(i => i.delivery_type_number === 1)),
        semestralCouponDiscount: couponDiscountForItems(itemsForImmediateDocument.filter(i => i.delivery_type_number === 2)),
        couponCode: reservedCoupon?.code,
        annualCouponItems: couponPdfItemsFor(itemsForImmediateDocument.filter(i => i.delivery_type_number === 1)),
        semestralCouponItems: couponPdfItemsFor(itemsForImmediateDocument.filter(i => i.delivery_type_number === 2)),
        annualQuantityInstallment: selectedAnnualInstallments,
        semestralQuantityInstallment: selectedSemestralInstallments,
        createdBy: user.email,
      });

      // GERAR PDFs e fazer o ZIP
      setTimeout(async () => {
        const dateStr = new Date().toISOString().split("T")[0];
        const pdfsToZip = [];

        let annualQuantityInstallment = selectedAnnualInstallments;
        let semestralQuantityInstallment = selectedSemestralInstallments;
        const date = new Date();

        // Se por algum motivo os estados estiverem indefinidos, buscamos o default da API como fallback
        if (annualQuantityInstallment === undefined || semestralQuantityInstallment === undefined) {
          try {
            const invoicingSummary = await invoicingApi.summary(date.getMonth() + 1, date.getFullYear());
            invoicingSummary.data.forEach((item: any) => {
              if (item.deliveryTypeId === 1 && annualQuantityInstallment === undefined) {
                annualQuantityInstallment = item.invoicingQtd;
              }
              if (item.deliveryTypeId === 2 && semestralQuantityInstallment === undefined) {
                semestralQuantityInstallment = item.invoicingQtd;
              }
            });
          } catch (e) {
            console.warn("Erro ao buscar parcelamento de faturamento para fallback no PDF", e);
          }
        }



        // Itens para PDF Anual
        const annualPDFItems = itemsForImmediateDocument.filter(i => i.delivery_type_number === 1 && (isNormal));
        if (annualOrderNumber && annualPDFItems.length > 0) {
          const annualPdfFilename = `Pedido_Anual_${annualOrderNumber}_${dateStr}.pdf`;
          const annualBlob = (await savedOrderPdf(annualOrderId, annualPdfFilename)) || await generateNativePDF({
            quantityInstallment: annualQuantityInstallment,
            annualQuantityInstallment: annualQuantityInstallment,
            filename: annualPdfFilename,
            orderNumber: annualOrderNumber,
            clientName: selectedClient.nome_cliente,
            clientCode: selectedClient.codigo_cnx,
            clientCNPJ: selectedClient.cnpj,
            clientAddress: selectedClient.endereco,
            clientNumber: selectedClient.numero,
            clientDistrict: selectedClient.bairro,
            clientCity: selectedClient.cidade,
            clientState: selectedClient.estado,
            clientPostCode: selectedClient.cep,
            ...pdfAddresses,
            orderItems: annualPDFItems,
            grossSubtotal: annualPDFItems.reduce((sum, item) => sum + item.preco * item.quantidade_pedido, 0),
            netSubtotal: annualPDFItems.reduce((sum, item) => sum + item.valor_liquido * item.quantidade_pedido, 0),
            total: annualPDFItems.reduce((sum, item) => sum + item.valor_total, 0) + freteAnual - couponDiscountForItems(annualPDFItems),
            couponDiscount: couponDiscountForItems(annualPDFItems),
            couponCode: reservedCoupon?.code,
            couponItems: couponPdfItemsFor(annualPDFItems),
            shippingTotal: freteAnual,
            createdBy: user.email,
          });
          if (annualBlob) pdfsToZip.push({ filename: annualPdfFilename, data: annualBlob });
        }

        // Itens para PDF Semestral/Simulado
        const semestralPDFItems = isNormal
          ? itemsForImmediateDocument.filter(i => i.delivery_type_number === 2)
          : itemsWithQuantity; // Programado ou Simulado

        if (semestralOrderNumber && semestralPDFItems.length > 0) {
          const label = isSimulado ? "Simulado" : "Semestral";
          const semestralPdfFilename = `Pedido_${label}_${semestralOrderNumber}_${dateStr}.pdf`;

          const semestralBlob = (await savedOrderPdf(semestralOrderId, semestralPdfFilename)) || await generateNativePDF({
            quantityInstallment: semestralQuantityInstallment,
            semestralQuantityInstallment: semestralQuantityInstallment,
            filename: semestralPdfFilename,
            orderNumber: semestralOrderNumber,
            clientName: selectedClient.nome_cliente,
            clientCode: selectedClient.codigo_cnx,
            clientCNPJ: selectedClient.cnpj,
            clientAddress: selectedClient.endereco,
            clientNumber: selectedClient.numero,
            clientDistrict: selectedClient.bairro,
            clientCity: selectedClient.cidade,
            clientState: selectedClient.estado,
            clientPostCode: selectedClient.cep,
            ...pdfAddresses,
            orderItems: semestralPDFItems,
            grossSubtotal: semestralPDFItems.reduce((sum, item) => sum + item.preco * item.quantidade_pedido, 0),
            netSubtotal: semestralPDFItems.reduce((sum, item) => sum + item.valor_liquido * item.quantidade_pedido, 0),
            total: semestralPDFItems.reduce((sum, item) => sum + item.valor_total, 0) + freteSemestral - couponDiscountForItems(semestralPDFItems),
            couponDiscount: couponDiscountForItems(semestralPDFItems),
            couponCode: reservedCoupon?.code,
            couponItems: couponPdfItemsFor(semestralPDFItems),
            shippingTotal: freteSemestral,
            createdBy: user.email,
          });
          if (semestralBlob) pdfsToZip.push({ filename: semestralPdfFilename, data: semestralBlob });
        }

        if (pdfsToZip.length > 0) {
          const zipFilename = `Pedidos_Conexia_${generatedOrderNumber}_${dateStr}.zip`;
          zipAndSave(pdfsToZip, zipFilename);
        }
      }, 500);
    } catch (error) {
      if (couponReservationId) {
        // Liberar a reserva também desfaz, no backend, os pedidos já criados com o cupom.
        try {
          await couponApi.release(couponReservationId);
        } catch (releaseError) {
          console.warn("Não foi possível liberar a reserva do cupom.", releaseError);
        }
      }
      console.error("Error finalizing order:", error);
      statusModalControls.setError(true);

      // Hide all progress indicators
      setProcessingProgress((prev) => ({ ...prev, isVisible: false }));

      const invalidCycleCode = isInvalidCycleCodeFormatError(error);
      let errorMessage = error?.response?.data?.message || 'Ops! Algo não saiu como esperado. Tente de novo. 😅';
      if (invalidCycleCode) {
        errorMessage = invalidCycleCode.message || 'Código do ciclo comercial do pedido está em formato inválido.';
      } else if (isCycleNotOpenError(error)) {
        errorMessage = error?.response?.data?.message || 'O ciclo comercial deste pedido ainda não está aberto para novos pedidos.';
      } else if (isCycleNotConfiguredError(error)) {
        errorMessage = error?.response?.data?.message || 'Não existe ciclo comercial cadastrado para o ano deste pedido.';
      }

      const formatError: Error = {
        message: errorMessage,
        name: 'Erro:',
      }

      const errorType = statusModalState.currentStep || "processing";
      statusModalControls.showError(formatError, errorType);
    }
  };

  // Função para download do PDF usando dados persistidos
  const handleDownloadPDF = async () => {
    if (!completedOrderData || !selectedClient) {
      toast.error("Erro no download", {
        description:
          "Dados do pedido não encontrados. Finalize um pedido primeiro.",
      });
      return;
    }

    setIsDownloadingPDF(true);

    try {
      const {
        orderItems,
        annualOrderNumber,
        semestralOrderNumber,
        orderNumber,
      } = completedOrderData;
      const dateStr = new Date().toISOString().split("T")[0];
      const pdfsToZip = [];

      // Separar itens anuais e semestrais
      const annualItems = orderItems.filter(
        (item) => item.delivery_type_number === 1
      );
      const semestralItems = orderItems.filter(
        (item) => item.delivery_type_number === 2
      );

      // Gerar PDF do pedido anual (se existir)
      if (annualOrderNumber && annualItems.length > 0) {
        const annualPdfFilename = `Pedido_Anual_${annualOrderNumber}_${dateStr}.pdf`;
        const annualTotal = annualItems.reduce(
          (sum, item) => sum + item.valor_total,
          0
        );
        const annualSubtotal = annualItems.reduce(
          (sum, item) => sum + item.valor_liquido * item.quantidade_pedido,
          0
        );

        const annualBlob = (await savedOrderPdf(completedOrderData.annualOrderId, annualPdfFilename)) || await generateNativePDF({
          quantityInstallment: completedOrderData.annualQuantityInstallment ?? 1,
          annualQuantityInstallment: completedOrderData.annualQuantityInstallment ?? 1,
          filename: annualPdfFilename,
          orderNumber: annualOrderNumber,
          clientName: selectedClient.nome_cliente,
          clientCode: selectedClient.codigo_cnx,
          clientCNPJ: selectedClient.cnpj,
          clientAddress: selectedClient.endereco,
          clientNumber: selectedClient.numero,
          clientDistrict: selectedClient.bairro,
          clientCity: selectedClient.cidade,
          clientState: selectedClient.estado,
          clientPostCode: selectedClient.cep,
          ...pdfAddresses,
          orderItems: annualItems,
          grossSubtotal: annualItems.reduce(
            (sum, item) => sum + item.preco * item.quantidade_pedido,
            0
          ),
          netSubtotal: annualSubtotal,
          total: Math.max(0, annualTotal + (completedOrderData.annualShippingTotal || 0) - (completedOrderData.annualCouponDiscount || 0)),
          couponDiscount: completedOrderData.annualCouponDiscount || 0,
          couponCode: completedOrderData.couponCode,
          couponItems: completedOrderData.annualCouponItems,
          shippingTotal: (completedOrderData.annualShippingTotal || 0),
          createdBy: completedOrderData.createdBy,
        });
        if (annualBlob) {
          pdfsToZip.push({ filename: annualPdfFilename, data: annualBlob });
        }
      }

      // Gerar PDF do pedido semestral (se existir)
      if (semestralOrderNumber && semestralItems.length > 0) {
        const semestralPdfFilename = `Pedido_Semestral_${semestralOrderNumber}_${dateStr}.pdf`;
        const semestralTotal = semestralItems.reduce(
          (sum, item) => sum + item.valor_total,
          0
        );
        const semestralSubtotal = semestralItems.reduce(
          (sum, item) => sum + item.valor_liquido * item.quantidade_pedido,
          0
        );

        const semestralBlob = (await savedOrderPdf(completedOrderData.semestralOrderId, semestralPdfFilename)) || await generateNativePDF({
          quantityInstallment: completedOrderData.semestralQuantityInstallment ?? 1,
          semestralQuantityInstallment: completedOrderData.semestralQuantityInstallment ?? 1,
          filename: semestralPdfFilename,
          orderNumber: semestralOrderNumber,
          clientName: selectedClient.nome_cliente,
          clientCode: selectedClient.codigo_cnx,
          clientCNPJ: selectedClient.cnpj,
          clientAddress: selectedClient.endereco,
          clientNumber: selectedClient.numero,
          clientDistrict: selectedClient.bairro,
          clientCity: selectedClient.cidade,
          clientState: selectedClient.estado,
          clientPostCode: selectedClient.cep,
          ...pdfAddresses,
          orderItems: semestralItems,
          grossSubtotal: semestralItems.reduce(
            (sum, item) => sum + item.preco * item.quantidade_pedido,
            0
          ),
          netSubtotal: semestralSubtotal,
          total: Math.max(0, semestralTotal + (completedOrderData.semestralShippingTotal || 0) - (completedOrderData.semestralCouponDiscount || 0)),
          couponDiscount: completedOrderData.semestralCouponDiscount || 0,
          couponCode: completedOrderData.couponCode,
          couponItems: completedOrderData.semestralCouponItems,
          shippingTotal: (completedOrderData.semestralShippingTotal || 0),
          createdBy: completedOrderData.createdBy,
        });
        if (semestralBlob) {
          pdfsToZip.push({
            filename: semestralPdfFilename,
            data: semestralBlob,
          });
        }
      }

      if (pdfsToZip.length > 0) {
        const zipFilename = `Pedidos_Conexia_${orderNumber}_${dateStr}.zip`;
        await zipAndSave(pdfsToZip, zipFilename);
        toast.success("Download concluído!", {
          description: `O arquivo ${zipFilename} foi baixado com sucesso.`,
        });
      } else {
        toast.warning("Nenhum PDF para baixar", {
          description:
            "Não foram encontrados itens para gerar os PDFs do pedido.",
        });
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erro no download", {
        description: "Ocorreu um erro inesperado ao gerar e baixar o PDF.",
      });
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleTypeChange = (value: string) => {
    setTypeLoading(true);
    if (typePedido !== null && typePedido !== value) {
      toast.info("Visualização de pedido alterada.", {
        description: `Agora você está vendo os pedidos do tipo ${value}.`,
      });
    }
    setTypePedido(value as OrderType);

    setTimeout(() => {
      setTypeLoading(false);
    }, 500);
  };


  return (
    <TooltipProvider>
      <CyclePickerDialog
        open={cycleDialogOpen}
        availableCycles={availableCycles}
        onOpenChange={setCycleDialogOpen}
        onSelect={selectCycle}
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-7xl">


          <ClientSelector
            isScheduled={typePedido == 'PROGRAMADO'}
            cycleYear={selectedCycleYear ?? availableOrderCycleYears[0] ?? new Date().getFullYear()}
          />

          {/* Card de Ciclo Comercial — seletor real: troca selectedCycleYear e recarrega o catálogo.
              Ciclos Closing também são selecionáveis, mas abrem a tela em modo somente leitura.
              Ciclos Published (view-only, ver viewOnlyCycleYears em UserContext) entram como card
              travado ao lado, com link para a tela de simulação — sem pedido possível ainda. */}
          {selectedClient && (selectableCycleYears.length > 0 || (viewOnlyCycleYears?.length ?? 0) > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {selectableCycleYears.map((year) => {
                const isSelected = (selectedCycleYear ?? availableOrderCycleYears[0]) === year;
                const isReadOnly = readOnlyCycleYears.includes(year);
                const CycleIcon = isReadOnly ? Eye : Package;
                const cycleHint = isReadOnly
                  ? (isSelected ? `Visualização do Ciclo ${year}` : "Clique para visualizar este ciclo")
                  : (isSelected ? "Realize seu pedido" : "Clique para trocar para este ciclo");
                // Ciclo encerrado (Closing) tem fundo/texto sempre em cinza — mais escuro quando
                // selecionado (mantendo a borda primary do selecionado), mais claro quando não —
                // o foco visual é o ciclo Open, onde o pedido pode ser feito.
                const cardTone = isSelected
                  ? isReadOnly
                    ? { card: "border-primary bg-gray-100 cursor-default", icon: "text-gray-500", title: "text-gray-600", hint: "text-gray-500" }
                    : { card: "border-primary bg-primary/5 cursor-default", icon: "text-primary", title: "text-primary", hint: "text-muted-foreground" }
                  : isReadOnly
                    ? { card: "border-gray-200 bg-gray-50/50 hover:border-gray-300 cursor-pointer", icon: "text-gray-300", title: "text-gray-400", hint: "text-gray-400" }
                    : { card: "border-gray-300 bg-gray-50 hover:border-primary/50 hover:bg-primary/5 cursor-pointer", icon: "text-gray-400", title: "text-gray-500", hint: "text-muted-foreground" };
                return (
                  <button
                    key={year}
                    type="button"
                    onClick={() => selectCycle(year)}
                    disabled={isSelected}
                    className={`relative rounded-lg border-2 p-4 text-left transition-colors ${cardTone.card}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CycleIcon className={`h-5 w-5 ${cardTone.icon}`} />
                        <span className={`font-semibold ${cardTone.title}`}>
                          Ciclo {year}
                        </span>
                      </div>
                      {isReadOnly ? (
                        null
                      ) : isSelected && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          ● Ativo
                        </span>
                      )}
                    </div>
                    <p className={`mt-1 text-xs ${cardTone.hint}`}>
                      {cycleHint}
                    </p>
                  </button>
                );
              })}
              {viewOnlyCycleYears?.map((year) => (
                <div
                  key={`view-only-${year}`}
                  className="relative rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 opacity-75 cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="h-5 w-5 text-gray-400" />
                      <span className="font-semibold text-gray-500">Ciclo {year}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                      Abertura: 01/10/{year - 1}
                    </span>
                  </div>
                  {/* <p className="mt-1 text-xs text-gray-400">Este ciclo ainda não está aberto.</p> */}
                  <Link
                    to="/customer-portal/realizar-pedido/2027-visualizacao"
                    className="mt-2 underline inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors bg-primary/5 hover:bg-primary/10 px-2.5 py-1 rounded-md border border-primary/20"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Eye className="h-3 w-3" />
                    Consultar solução {year}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              ))}
            </div>
          )}

          {selectedClient === null && (
            <div className="py-12 text-center border-2  transition-all duration-200 mb-8 rounded-lg">
              <SchoolIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">
                Selecione uma unidade escolar.
              </h3>
            </div>
          )}

          {
            selectedClient && (
              <>
                {typePedido === null ? (
                  <div className="py-12 text-center transition-all duration-200 mb-8 rounded-lg">
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-gray-800">Selecione o Tipo de Pedido</h3>
                      <p className="text-muted-foreground mt-2">
                        Para prosseguir, escolha o tipo de pedido que deseja realizar. Lembre-se que você pode fazer apenas um pedido por vez.
                      </p>
                    </div>
                    <RadioGroup
                      value={typePedido || ""}
                      onValueChange={handleTypeChange}
                      className="grid max-w-3xl grid-cols-1 gap-6 mx-auto md:grid-cols-2"
                    >
                      <Label
                        htmlFor="NORMAL"
                        className={`flex flex - col items - center justify - center rounded - lg border - 2 p - 6 cursor - pointer transition - all duration - 300 hover: shadow - lg hover: border - blue - 500 h - full ${typePedido === 'NORMAL' ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200 bg-white'
                          } `}
                      >
                        <RadioGroupItem value="NORMAL" id="NORMAL" className="sr-only" />
                        <span className="text-2xl font-bold text-blue-700">Pedido Principal/Complementar</span>
                        <span className="mt-2 text-sm text-center text-gray-600">
                          Para visualizar e ajustar itens do pedido principal e/ou complementar.
                        </span>
                      </Label>
                      <Label
                        htmlFor="PROGRAMADO"
                        className={`flex flex - col items - center justify - center rounded - lg border - 2 p - 6 cursor - pointer transition - all duration - 300 hover: shadow - lg hover: border - purple - 500 h - full ${typePedido === 'PROGRAMADO' ? 'border-purple-600 bg-purple-50 shadow-md' : 'border-gray-200 bg-white'
                          } `}
                      >
                        <RadioGroupItem value="PROGRAMADO" id="PROGRAMADO" className="sr-only" />
                        <span className="text-2xl font-bold text-purple-700">Pedido Programado</span>
                        <span className="mt-2 text-sm text-center text-gray-600">
                          Para visualizar e ajustar itens do 2 Semestre.
                        </span>
                      </Label>

                    </RadioGroup>
                  </div>
                ) : (
                  <>
                    {productsLoading || typeLoading ? (
                      <div className="flex flex-col items-center justify-center py-24">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        <p className="mt-4 text-muted-foreground">Carregando produtos...</p>
                      </div>
                    ) : (
                      <>
                        {itemsToDisplay.length > 0 && (
                          <OrderTimeline
                            orderItems={itemsToDisplay}
                            fullOrderItems={orderItems} // New prop
                            updateQuantidade={updateQuantidade}
                            updateQuantidadesBatch={updateQuantidadesBatch} // New prop
                            formatCurrency={formatCurrency}
                            finalizarPedido={finalizarPedido}
                            getGrossSubtotal={getGrossSubtotal}
                            getSubtotal={getSubtotal}
                            getTotal={getTotal}
                            freteAnual={freteAnual}
                            freteSemestral={freteSemestral}
                            shippingFee={selectedClient.shipping_fee}
                            isScheduled={isScheduled}
                            isSimulated={typePedido === "SIMULADO"}
                            isBackofficeEdit={!!location.state?.isBackofficeEdit}
                            isEditMode={!!scheduledOrderId}
                            onAnnualInstallmentsChange={setSelectedAnnualInstallments}
                            onSemestralInstallmentsChange={setSelectedSemestralInstallments}
                            onAnnualConditionChange={setSelectedAnnualCondition}
                            onSemestralConditionChange={setSelectedSemestralCondition}
                            cycleYear={selectedCycleYear ?? availableOrderCycleYears[0] ?? new Date().getFullYear()}
                            isReadOnly={isReadOnlyCycle}
                            shippingAddressSlot={isCustomerGroupAddressEnabled ? (
                              <OrderShippingAddressSelect
                                addresses={shippingAddress.addresses}
                                selectedAddress={shippingAddress.selectedAddress}
                                onSelect={shippingAddress.selectAddress}
                                isLoading={shippingAddress.isLoading}
                                isError={shippingAddress.isError}
                                disabled={isReadOnlyCycle}
                                recipientName={selectedClient?.nome_cliente}
                              />
                            ) : null}
                          />
                        )}
                        {itemsToDisplay.length === 0 && (
                          <div className="text-center py-12">
                            <div className="text-6xl mb-4">📦</div>
                            <h3 className="text-xl font-semibold text-muted-foreground mb-2">
                              Nenhum produto encontrado
                            </h3>
                          </div>
                        )}
                      </>
                    )}

                    <StatusModal
                      isVisible={statusModalState.isVisible}
                      state={statusModalState}
                      onRetry={() => {
                        statusModalControls.retry();
                        finalizarPedido();
                      }}
                      onDownloadPDF={handleDownloadPDF}
                      onNewOrder={() => {
                        const wasCompleted = statusModalState.currentStep === 'completed';
                        if (wasCompleted && scheduledOrderId) {
                          // Edição concluída: após a avaliação, recarrega a tela para atualizar todas as APIs.
                          statusModalControls.hide();
                          setPostCsatAction("reload");
                          setIsCsatModalOpen(true);
                          return;
                        }
                        setTypePedido('NORMAL');
                        statusModalControls.newOrder();
                        fetchOrderItems();
                        if (wasCompleted) setIsCsatModalOpen(true);
                      }}
                      onClose={() => {
                        const wasCompleted = statusModalState.currentStep === 'completed';
                        if (wasCompleted && scheduledOrderId) {
                          // Edição de pedido programado concluída: após a avaliação, volta para a tela
                          // de origem, que remonta e recarrega a listagem com os dados atualizados.
                          statusModalControls.hide();
                          setPostCsatAction("return");
                          setIsCsatModalOpen(true);
                          return;
                        }
                        if (!statusModalControls.isError) {
                          setTypePedido('NORMAL');
                          statusModalControls.newOrder();
                          fetchOrderItems();
                        }
                        statusModalControls.hide();
                        if (wasCompleted) setIsCsatModalOpen(true);
                      }}
                    />

                    <CsatRatingModal
                      isOpen={isCsatModalOpen}
                      onClose={() => {
                        setIsCsatModalOpen(false);
                        const action = postCsatAction;
                        if (!action) return;
                        setPostCsatAction(null);
                        if (action === "return") {
                          navigate(
                            location.state?.returnTo
                              ?? (location.state?.isBackofficeEdit
                                ? "/backoffice/pedido-programados"
                                : "/customer-portal/pedido-programado"),
                            { replace: true },
                          );
                        } else {
                          // Limpa o state da rota (senão a edição reabriria) e força o recarregamento completo.
                          window.history.replaceState(null, "", window.location.pathname);
                          window.location.reload();
                        }
                      }}
                      orderNumber={csatOrderNumber}
                    />

                    <OrderProgress
                      isVisible={processingProgress.isVisible}
                      currentBatch={processingProgress.currentBatch}
                      totalBatches={processingProgress.totalBatches}
                      currentItem={processingProgress.currentItem}
                      totalItems={processingProgress.totalItems}
                      message={processingProgress.message}
                    />

                    {showPDFSummary && selectedClient && orderNumber && (
                      <div
                        className="fixed top-0 left-0 w-full h-full bg-white z-50 overflow-auto"
                        style={{ transform: "translateX(-9999px)" }}
                      >
                        <div id="pdf-summary-content">
                          <PDFSummary
                            orderNumber={orderNumber}
                            clientName={selectedClient.nome_cliente}
                            clientCode={selectedClient.codigo_cnx}
                            orderItems={orderItems.filter((item) => item.quantidade_pedido > 0)}
                            grossSubtotal={getGrossSubtotal()}
                            netSubtotal={getSubtotal()}
                            total={getTotal()}
                            isScheduled={isScheduled}
                            annualQuantityInstallment={selectedAnnualInstallments}
                            semestralQuantityInstallment={selectedSemestralInstallments}
                            cycleYear={selectedCycleYear ?? availableOrderCycleYears[0] ?? new Date().getFullYear()}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )
          }
        </div >
      </div >
    </TooltipProvider >
  );
};

export default OrderInterface;
